<template>
	<div class="toast-stack" role="status" aria-live="polite">
		<div v-if="isMounted" class="toast" :class="toastClasses">
			<!-- Text 狀態：單行文字膠囊，沒有 icon -->
			<span v-if="isText">{{ current.message || current.title }}</span>
			<!-- 完整狀態：icon + 標題 / 說明 + 選配 Action -->
			<template v-else>
				<span class="toast-icon" aria-hidden="true" v-html="statusIcon" />
				<span class="toast-body">
					<span v-if="headline" class="toast-title">{{ headline }}</span>
					<span v-if="current.description" class="toast-desc">{{ current.description }}</span>
				</span>
				<button v-if="current.actionText" class="toast-action" type="button" @click="handleActionClick">
					{{ current.actionText }}
				</button>
			</template>
		</div>
	</div>
</template>
<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref } from 'vue'
import { icons } from '@shared-assets/icons-v2'

/**
 * @type {ToastType} Toast 的四種狀態，不帶就是純文字膠囊
 */
export type ToastType = 'success' | 'warning' | 'error' | 'loading'

/**
 * @interface CustomToastOpts 呼叫 $toast 時可帶的選項
 */
export interface CustomToastOpts {
	/**
	 * Toast Type，不帶就是 Text 膠囊
	 */
	type?: ToastType
	/**
	 * Title
	 */
	title?: string
	/**
	 * Message，Text 狀態的內容；帶 type 時當標題
	 */
	message?: string
	/**
	 * Description
	 */
	description?: string
	/**
	 * Action Text
	 */
	actionText?: string
	/**
	 * Actions event
	 */
	onAction?: () => void
	/**
	 * 不自動消失，要自己呼叫 hideToast，Loading 一定要用
	 */
	sticky?: boolean
}

/**
 * 一般 toast 停留毫秒數
 */
const VISIBLE_MS = 3000

/**
 * 離場動畫毫秒數
 */
const LEAVE_MS = 220

/**
 * 目前顯示的這一則的 id，hideToast 帶 id 時用來比對
 */
let currentId = 0
/**
 * 遞增的 toast id 計數器
 */
let idSeq = 0

/**
 * 目前這一則的內容
 */
const current = reactive<CustomToastOpts>({})
/**
 * 是否有 toast 節點
 */
const isMounted = ref(false)
/**
 * 是否正在離場
 */
const isLeaving = ref(false)
/**
 * 開始離場的計時器
 */
let hideTimer: ReturnType<typeof setTimeout> | undefined
/**
 * 移除節點的計時器
 */
let removeTimer: ReturnType<typeof setTimeout> | undefined

/**
 * 不帶 type 就是 Text 膠囊
 */
const isText = computed(() => !current.type)
/**
 * 完整狀態的標題，title 優先，沒有就用 message
 */
const headline = computed(() => current.title || current.message)
/**
 * 完整狀態的 icon，Text 狀態沒有
 */
const statusIcon = computed(() => (current.type ? icons[`status-${current.type}`] : ''))
/**
 * 外層 class：盒子種類、狀態、有無 Action 與離場
 */
const toastClasses = computed(() => [
	isText.value ? 'is-text' : 'is-full',
	current.type,
	{
		'has-action': !isText.value && !!current.actionText,
		'is-leaving': isLeaving.value,
	},
])

/**
 * 清掉兩個計時器
 * @return {void}
 */
const clearTimers = (): void => {
	clearTimeout(hideTimer)
	clearTimeout(removeTimer)
}

/**
 * 開始離場，等離場動畫跑完再移除節點
 * @return {void}
 */
const leave = (): void => {
	// 先清掉舊計時器，避免重複觸發
	clearTimers()
	// 沒有節點就不用離場
	if (!isMounted.value) {
		return
	}
	// 掛上離場 class
	isLeaving.value = true
	// 動畫跑完才移除節點
	removeTimer = setTimeout(() => {
		isMounted.value = false
		isLeaving.value = false
	}, LEAVE_MS)
}

/**
 * 點擊 Action 按鈕：先做事，再把 toast 收掉
 * @return {void}
 */
const handleActionClick = (): void => {
	// 呼叫 onAction
	current.onAction?.()
	// 收掉 toast
	leave()
}

/**
 * Show Toast，同一時間只留一則，新的沿用同一個節點
 * @param conf {CustomToastOpts}
 * @return {number} 這一則的 id，之後可交給 hideToast 只收自己這則
 */
const showToast = (conf: CustomToastOpts): number => {
	// 記下這一則的 id
	currentId = ++idSeq
	// 清掉上一則的計時器，舊的不能誤殺新的
	clearTimers()
	// 換掉內容：先清空再寫入，沒傳的欄位不會留著上一則的值
	Object.keys(current).forEach((key) => delete current[key as keyof CustomToastOpts])
	Object.assign(current, conf)
	// 拉回正在離場的那一則
	isLeaving.value = false
	// 確保節點存在
	isMounted.value = true

	// sticky 不自動收，等呼叫端自己 hideToast
	if (!conf.sticky) {
		// 一般 toast 3 秒後離場
		hideTimer = setTimeout(leave, VISIBLE_MS)
	}

	return currentId
}

/**
 * 收掉目前那一則，sticky 的 Loading toast 一定要自己呼叫
 * @param id {number} 帶了就只在目前顯示的是這個 id 才收，不帶就直接收
 * @return {void}
 */
const hideToast = (id?: number): void => {
	// 帶了 id 但目前顯示的已經是別則時
	if (id !== undefined && id !== currentId) {
		// 不是自己那則，不收
		return
	}

	// 收掉目前這一則
	leave()
}

// 卸載時清掉計時器
onBeforeUnmount(clearTimers)

// 對外暴露方法
defineExpose({ showToast, hideToast })
</script>
<style lang="scss">
@use '@shared-assets/scss/variables' as *;

// 疊層：不擋點擊，Action 自己開 pointer-events
.toast-stack {
	position: fixed;
	top: 64px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 3000;
	display: flex;
	flex-direction: column;
	gap: 8px;
	align-items: center;
	width: 100%;
	pointer-events: none;
}

// Text 狀態：膠囊
.toast {
	display: flex;
	align-items: center;
	justify-content: center;
	max-width: min(90%, 480px);
	padding: 12px 20px;
	border-radius: var(--radius-toast-text);
	background: var(--ui-grey-darkest);
	color: var(--ui-white);
	font-size: 17px;
	font-weight: 400;
	line-height: 1.4;
	text-align: center;
	box-shadow: var(--shadow-toast);
	// 進場用 animation，離場用 transition，兩者分開重複觸發才不會打架
	animation: toastIn 0.22s cubic-bezier(0, 0, 0.2, 1);
	transition: opacity 0.22s ease-in, transform 0.22s ease-in;

	&.is-leaving {
		opacity: 0;
		transform: translateY(-8px);
	}

	// 完整狀態：長方形，寬度跟著內容走，短字不低於 240、長字由 max-width 擋
	// 無 Action 右內距 20；有 Action 時 12，因為 Action 自己帶 12
	&.is-full {
		gap: 16px;
		justify-content: flex-start;
		width: auto;
		min-width: 240px;
		min-height: 60px;
		padding: 12px 20px;
		border-radius: var(--radius-toast-full);
		text-align: left;

		&.has-action {
			padding-right: 12px;
		}
	}

	.toast-icon {
		flex-shrink: 0;
		width: 24px;
		height: 24px;

		svg {
			display: block;
			width: 100%;
			height: 100%;
		}
	}

	&.success .toast-icon {
		color: var(--status-success);
	}

	&.warning .toast-icon {
		color: var(--status-warning);
	}

	&.error .toast-icon {
		color: var(--status-error);
	}

	// Loading 用白色並旋轉
	&.loading .toast-icon {
		color: var(--ui-white);
		animation: toastSpin 0.8s linear infinite;
	}
}

// 標題與說明，三個部位可單獨關掉，用 gap 才不會留空隙
// basis 用 auto 盒子才會貼著文字收，用 0 會在 hug 容器裡被壓成 0 寬
.toast-body {
	flex: 0 1 auto;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.toast-title {
	font-size: 15px;
	font-weight: 700;
	color: var(--ui-white);
}

.toast-desc {
	font-size: 13px;
	font-weight: 400;
	color: var(--ui-grey-lighter);
}

.toast-action {
	flex-shrink: 0;
	padding: 8px 12px;
	background: none;
	border: none;
	border-radius: 8px;
	color: var(--ui-white);
	font-family: inherit;
	font-size: 14px;
	font-weight: 510;
	white-space: nowrap;
	cursor: pointer;
	// 疊層不擋點擊，所以唯一能點的 Action 自己打開
	pointer-events: auto;

	&:hover {
		background: rgba(255, 255, 255, 0.12);
	}

	&:active {
		background: rgba(255, 255, 255, 0.2);
	}
}

@keyframes toastSpin {
	to {
		transform: rotate(360deg);
	}
}

@keyframes toastIn {
	from {
		opacity: 0;
		transform: translateY(-8px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

// 窄畫面：上限改跟著畫面，兩側各留 16
@media (max-width: #{$breakpoint-sm}) {
	.toast.is-full {
		max-width: calc(100% - 32px);
	}
}

// 要求減少動態：Loading 不轉，只留淡入淡出不位移
@media (prefers-reduced-motion: reduce) {
	.toast {
		animation: none;
		transition: opacity 0.22s linear;

		&.is-leaving {
			transform: none;
		}

		&.loading .toast-icon {
			animation: none;
		}
	}
}

// 載入中：整頁不再接受點擊，按鈕降透明度（版面不跳）
.is-loading {
	.language-item,
	.star-button {
		pointer-events: none;
	}

	button:disabled {
		opacity: 0.45;
		cursor: default;
	}
}
</style>
